/**
 * Error classification utilities
 *
 * Helps distinguish Safari PWA quirks from real application errors.
 */

import { createLogger } from '@/utils/logger';

const logger = createLogger('ErrorUtils');

/**
 * Check if an error is Safari's "Load failed" network error.
 *
 * Safari on iOS throws `TypeError: Load failed` during PWA suspend/resume cycles.
 * This is a benign network quirk, not a real application error.
 *
 * @param err - The caught error (unknown type per catch clause convention)
 * @returns true if this is Safari's Load failed error, false otherwise
 */
export function isSafariLoadFailed(err: unknown): boolean {
  return err instanceof TypeError && err.message.includes('Load failed');
}

/**
 * Log an error from invalidateQueries with appropriate severity.
 *
 * Safari's "Load failed" errors are expected during PWA lifecycle → log as debug.
 * All other errors (auth failures, server errors, real network issues) → log as error.
 *
 * @param context - Context string for the log message (e.g., hook name)
 * @param err - The caught error
 */
export function logInvalidateQueriesError(context: string, err: unknown): void {
  if (isSafariLoadFailed(err)) {
    logger.debug(`[${context}] Safari Load failed (expected during PWA lifecycle)`, err);
  } else {
    logger.error(`[${context}] invalidateQueries error`, err);
  }
}
